import Feature from "ol/Feature";
import { LineString } from "ol/geom";
import { createLinearInterpolator } from "../style/util/linearInterpolation";
import { lrDecode } from "../style/util/lrDecoding";

export const CURVATURE_LINEAR_RECONSTRUCTED_TYPE =
  "curvature-linear-reconstructed";

/**
 * Handler that reconstructs curve geometries from curvature:linear tag values.
 *
 * The curvature:linear tag has the format: "0#1;50#300;100#0"
 *
 * The curve is reconstructed by integrating curvature along the geometry,
 * starting from the feature's start point. Afterwards the curve is rotated and
 * scaled to end in the feature's end point.
 */

export const curvatureLinearHandler = (event) => {
  const features = event.target.getFeatures();
  features.forEach((feature) => {
    if (feature.getGeometry()?.getType() !== "LineString") {
      return;
    }

    // The curvature linear value is stored as "orbis-curvature:linear" on the feature
    let curvatureLinearValue = feature.get("orbis-curvature:linear");
    if (!curvatureLinearValue || !curvatureLinearValue.includes("#")) {
      return;
    }

    // Parse the curvature:linear tag using lrDecode
    // Format: "0#1;50#300;100#0"
    const lrChain = lrDecode("orbis-curvature:linear", curvatureLinearValue);
    const dataPoints = lrChain.elements.map((element) => ({
      distance: element.getStartDistance(),
      value: Number(element.getValue()),
    }));
    if (dataPoints.length < 2) {
      return;
    }

    // Get the feature's geometry to determine start point and end point
    const geometry = feature.getGeometry();
    const coordinates = geometry.getCoordinates();
    if (coordinates.length < 2) {
      return;
    }

    // Start point and end point in EPSG:3857
    const startPoint = coordinates[0];
    const endPoint = coordinates[coordinates.length - 1];

    // Total length of the curvature data in centimeters
    const totalLengthCm = dataPoints[dataPoints.length - 1].distance;
    if (totalLengthCm <= 0) {
      return;
    }

    // We need to convert from real-world cm to EPSG:3857 units.
    // EPSG:3857 meters per real meter depends on latitude.
    // Use the start point to compute the scale factor.
    const mercatorScale = getMercatorScale(startPoint[1]);

    // Reconstruct the curve in a local coordinate system (origin at startPoint,
    // initial heading = 0), so we can later fit it to the actual start/end points.
    const localCoords = reconstructCurveLocal(dataPoints, mercatorScale);

    // Fit the local curve to the actual start→end vector via rotation + uniform scale.
    const reconstructedCoords = fitCurveToEndpoints(
      localCoords,
      startPoint,
      endPoint,
    );

    if (reconstructedCoords.length < 2) {
      return;
    }

    // Create a new feature for the reconstructed curve
    const curveFeature = new Feature({
      geometry: new LineString(reconstructedCoords),
    });
    curveFeature.set("type", CURVATURE_LINEAR_RECONSTRUCTED_TYPE);
    curveFeature.set("source-feature-id", feature.getId());
    curveFeature.set("nonInteractive", true);

    event.target.addFeature(curveFeature);
  });
};

/**
 * Get the Mercator scale factor at a given Y coordinate in EPSG:3857.
 * In EPSG:3857, 1 unit at the equator = 1 meter, but at other latitudes
 * the scale is sec(latitude). So to convert real-world meters to 3857 units:
 * epsg3857_units = real_meters * mercatorScale
 */
function getMercatorScale(y3857) {
  // Convert 3857 Y to latitude in radians
  // lat = 2 * atan(exp(y / 6378137)) - pi/2
  const EARTH_RADIUS = 6378137;
  const latRad = 2 * Math.atan(Math.exp(y3857 / EARTH_RADIUS)) - Math.PI / 2;
  // Mercator scale = 1 / cos(lat)
  return 1 / Math.cos(latRad);
}

/**
 * Reconstruct a curve from curvature data points in a local coordinate system.
 * The curve starts at the origin [0, 0] with an initial heading of 0 (pointing
 * along the positive X axis). The caller is responsible for rotating, scaling,
 * and translating the result to world coordinates via fitCurveToEndpoints().
 *
 * @param {Array<{distance: number, value: number}>} dataPoints - Curvature data (distance in cm, value is curvature)
 * @param {number} mercatorScale - Scale factor to convert real meters to EPSG:3857 units
 * @returns {Array<number[]>} Array of [x, y] coordinates in local space
 */
function reconstructCurveLocal(dataPoints, mercatorScale) {
  const totalLengthCm = dataPoints[dataPoints.length - 1].distance;
  const interpolateCurvature = createLinearInterpolator(dataPoints);

  // Use a step size of about 10 cm for smooth curves, but at least 100 steps
  const numSteps = Math.max(100, Math.ceil(totalLengthCm / 10));
  const dsCm = totalLengthCm / numSteps; // step size in cm
  const dsM = dsCm / 100; // step size in real-world meters

  const points = [[0, 0]];
  let x = 0;
  let y = 0;
  let heading = 0; // start heading = 0 (along positive X axis)

  for (let i = 0; i < numSteps; i++) {
    const distanceCm = (i + 0.5) * dsCm; // midpoint of step for better accuracy

    // Get interpolated curvature at this distance
    const curvatureValue = interpolateCurvature(distanceCm);

    // Convert curvature value to 1/m: κ = curvatureValue / 10000
    const kappa = curvatureValue / 10000;

    // Update heading: dθ = κ * ds (in meters)
    heading += kappa * dsM;

    // Step forward
    const ds3857 = dsM * mercatorScale;
    x += Math.cos(heading) * ds3857;
    y += Math.sin(heading) * ds3857;

    points.push([x, y]);
  }

  return points;
}

/**
 * Rotate and uniformly scale a locally-reconstructed curve so that it starts at
 * `startPoint` and its last point coincides exactly with `endPoint`.
 *
 * The local curve already starts at [0, 0]. We compute the rotation angle and
 * scale factor that map the local endpoint to the vector (endPoint - startPoint),
 * then apply the same transform to every point and translate to world space.
 *
 * @param {Array<number[]>} localCoords - Points in local space (first point is origin)
 * @param {number[]} startPoint - [x, y] world coordinates for the curve start
 * @param {number[]} endPoint - [x, y] world coordinates the curve end must hit
 * @returns {Array<number[]>} Transformed coordinates in EPSG:3857
 */
function fitCurveToEndpoints(localCoords, startPoint, endPoint) {
  const localEnd = localCoords[localCoords.length - 1];
  const localEndX = localEnd[0];
  const localEndY = localEnd[1];

  // Vector the curve must match in world space
  const targetDX = endPoint[0] - startPoint[0];
  const targetDY = endPoint[1] - startPoint[1];

  const localLen = Math.sqrt(localEndX * localEndX + localEndY * localEndY);
  const targetLen = Math.sqrt(targetDX * targetDX + targetDY * targetDY);

  // If either length is (near) zero, just return a straight line
  if (localLen < 1e-10 || targetLen < 1e-10) {
    return localCoords.map((_, i) => {
      const t = i / (localCoords.length - 1);
      return [startPoint[0] + t * targetDX, startPoint[1] + t * targetDY];
    });
  }

  // Rotation angle: angle of target vector minus angle of local endpoint vector
  const localAngle = Math.atan2(localEndY, localEndX);
  const targetAngle = Math.atan2(targetDY, targetDX);
  const rotation = targetAngle - localAngle;

  // Uniform scale factor
  const scale = targetLen / localLen;

  const cosR = Math.cos(rotation);
  const sinR = Math.sin(rotation);

  return localCoords.map(([lx, ly]) => {
    const rx = lx * cosR - ly * sinR;
    const ry = lx * sinR + ly * cosR;
    return [startPoint[0] + rx * scale, startPoint[1] + ry * scale];
  });
}
